const pickFirstString = (...values) =>
  values.find(value => typeof value === 'string' && value.trim()) || '';

const USER_PREFERENCES_KEY = 'userProfilePreferences';

const buildPermissionGroups = permissions =>
  permissions.reduce((groups, permissionCode) => {
    if (typeof permissionCode !== 'string') return groups;

    const [module = 'general', ...actionParts] = permissionCode.split('.');
    const action = actionParts.join('.') || 'view';

    if (!groups[module]) {
      groups[module] = [];
    }

    groups[module].push({
      code: permissionCode,
      module,
      action
    });

    return groups;
  }, {});

export const getUserProfileData = userData => {
  const user = userData?.user || {};
  const membership = userData?.membership || {};
  const company = userData?.company || {};
  const profile = userData?.profile || {};

  const firstName = pickFirstString(
    user.first_name,
    membership.first_name
  );
  const lastName = pickFirstString(
    user.last_name,
    membership.last_name
  );
  const username = pickFirstString(
    user.username,
    membership.username,
    userData?.userName
  );
  const email = pickFirstString(
    user.email,
    membership.email,
    userData?.userEmail
  );
  const companyName = pickFirstString(
    company.name,
    membership.company_name,
    membership.company?.name
  );
  const profileName = pickFirstString(
    profile.name,
    membership.profile_name,
    membership.profile?.name
  );
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const displayName = fullName || username || email || 'User';

  const initialsSource = [firstName, lastName].filter(Boolean);
  const initials =
    initialsSource.length > 0
      ? initialsSource.map(value => value.trim().charAt(0).toUpperCase()).join('').slice(0, 2)
      : pickFirstString(username, email, 'U').slice(0, 2).toUpperCase();

  const membershipStatus = membership.status ?? membership.is_active;
  const joinedAt = membership.created_at || membership.createdAt || user.created_at || user.createdAt || '';

  return {
    user,
    membership,
    company,
    profile,
    firstName,
    lastName,
    fullName,
    displayName,
    initials,
    username,
    email,
    companyName,
    profileName,
    membershipStatus,
    joinedAt,
    permissions: Array.isArray(userData?.permissions) ? userData.permissions : []
  };
};

export const getGroupedPermissions = permissions => {
  const groupedPermissions = buildPermissionGroups(
    Array.isArray(permissions) ? permissions : []
  );

  return Object.entries(groupedPermissions).map(([module, items]) => ({
    module,
    items
  }));
};

export const getUserPreferences = userId => {
  if (typeof window === 'undefined' || !userId) {
    return {
      phone: '',
      heading: '',
      intro: ''
    };
  }

  try {
    const savedPreferences = JSON.parse(
      window.localStorage.getItem(USER_PREFERENCES_KEY) || '{}'
    );

    return savedPreferences[userId] || {
      phone: '',
      heading: '',
      intro: ''
    };
  } catch (error) {
    return {
      phone: '',
      heading: '',
      intro: ''
    };
  }
};

export const saveUserPreferences = (userId, preferences) => {
  if (typeof window === 'undefined' || !userId) return;

  try {
    const savedPreferences = JSON.parse(
      window.localStorage.getItem(USER_PREFERENCES_KEY) || '{}'
    );

    window.localStorage.setItem(
      USER_PREFERENCES_KEY,
      JSON.stringify({
        ...savedPreferences,
        [userId]: preferences
      })
    );
  } catch (error) {
    window.localStorage.setItem(
      USER_PREFERENCES_KEY,
      JSON.stringify({
        [userId]: preferences
      })
    );
  }
};
